import React, { useState, useEffect, useMemo } from 'react';
import {
    Modal,
    Stack,
    Title,
    Group,
    Button,
    Text,
    TextInput,
    Select,
    Textarea,
    ScrollArea,
    Checkbox,
    Table,
    Badge,
    Divider,
    Paper,
    createStyles,
    Tooltip,
    SelectItem,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconCurrencyRupee, IconCalendar, IconCheck } from '@tabler/icons-react';
import { EmployeePayrollRecord } from './PayrollEmployeesPage'; // Importing the exported interface
import { collectionGroup, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";

const useStyles = createStyles((theme) => ({
    header: {
        position: 'sticky',
        top: 0,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
        transition: 'box-shadow 150ms ease',
        zIndex: 1,
    },

    scrolled: {
        boxShadow: theme.shadows.sm,
    },

    summaryCard: {
        backgroundColor: theme.colors.blue[0],
        color: theme.colors.blue[9],
        border: `1px solid ${theme.colors.blue[2]}`,
    },

    detailsSection: {
        borderTop: `1px solid ${theme.colors.gray[2]}`,
        paddingTop: theme.spacing.md,
        marginTop: theme.spacing.md,
    }
}));

interface RecordPayrollPaymentModalProps {
    opened: boolean;
    onClose: () => void;
    employees: EmployeePayrollRecord[];
    onConfirm: (selectedEmployeeIds: string[], paymentDetails: any) => Promise<void>;
}

export default function RecordPayrollPaymentModal({
    opened,
    onClose,
    employees,
    onConfirm
}: RecordPayrollPaymentModalProps) {
    const { classes, cx } = useStyles();
    const [submitting, setSubmitting] = useState(false);
    const companyId = useAppSelector((state: RootState) => state.session.company_id);
    const [paymentAccountList, setPaymentAccountList] = useState<any[]>([]);
    const [paidThroughId, setPaidThroughId] = useState<string | null>(null);

    const fetchBankingData = async () => {
        try {
            if (!companyId) return;

            // Fetch banking data from Firestore
            const companySettingsRef = collectionGroup(db, "banking");
            const q = query(companySettingsRef, where("company_id", "==", companyId));

            const querySnapshot = await getDocs(q);
            let bankingData: any[] = [];

            if (!querySnapshot.empty) {
                bankingData = querySnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                }));
            }

            // Fetch Cash Payment Data
            const cashDocRef = query(
                collectionGroup(db, "paymentcollection"),
                where("type", "==", "cash"),
                where("company_id", "==", companyId),
                limit(1)
            );

            const cashDocSnap = await getDocs(cashDocRef);

            if (!cashDocSnap.empty) {
                const cashData = cashDocSnap.docs[0].data();
                if (cashData?.status === "Active") {
                    bankingData.push({
                        id: 'petty_cash', // Using a fixed ID for logic check later if needed
                        value: "petty_cash",
                        label: "Petty Cash",
                        accountName: "Petty Cash"
                    });
                }
            }

            setPaymentAccountList(bankingData);
        } catch (error) {
            console.error("Error fetching banking data:", error);
        }
    };

    useEffect(() => {
        if (opened && companyId) {
            fetchBankingData();
        }
    }, [opened, companyId]);

    // Derived state for default selection (only unpaid employees)
    // We filter employees who are NOT 'Paid' to be selectable by default, 
    // but we show all passed employees or maybe just the relevant ones?
    // The requirement is "Record Payment". Usually you want to see people who need payment.
    // Let's assume the parent might pass all, so we filter for display or selection.

    // Let's show all employees passed in, but default select the ones that are NOT paid.
    const unpaidEmployees = useMemo(() =>
        employees.filter(emp => emp.payment_status !== 'Paid'),
        [employees]);

    const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([]);

    // Form State
    const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
    const [paymentMode, setPaymentMode] = useState<string | null>('Bank Transfer');
    const [referenceId, setReferenceId] = useState('');
    const [notes, setNotes] = useState('');
    // const [paymentAccountList, setPaymentAccountList] = useState<any>([]); // This was a duplicate declaration
    // const [paidThrouhID, setPaidThrouhID] = useState<any>(""); // This was a duplicate declaration

    // Update selection when modal opens or employees change
    useEffect(() => {
        if (opened) {
            setSelectedEmployeeIds(unpaidEmployees.map(e => e.id));
            setPaymentDate(new Date());
            setReferenceId('');
            setNotes('');
            setPaymentMode('Bank Transfer');
        }
    }, [opened, unpaidEmployees]);



    const calculateTotal = () => {
        const selected = employees.filter(emp => selectedEmployeeIds.includes(emp.id));
        console.log("RecordPaymentModal - All Employees:", employees);
        console.log("RecordPaymentModal - Selected IDs:", selectedEmployeeIds);
        console.log("RecordPaymentModal - Selected Employees:", selected);

        const total = selected.reduce((sum, emp) => {
            const amount = emp.calculated_salary?.monthly_ctc || 0;
            console.log(`Emp: ${emp.name}, Amount: ${amount}, Raw:`, emp.calculated_salary);
            return sum + amount;
        }, 0);
        console.log("RecordPaymentModal - Total:", total);
        return total;
    };

    const handleSubmit = async () => {
        if (selectedEmployeeIds.length === 0) return;

        try {
            setSubmitting(true);
            const selectedAccount = paymentAccountList.find(acc => (acc.id || acc.value) === paidThroughId);
            const accountName = selectedAccount?.accountName || selectedAccount?.label || selectedAccount?.nickname || "Unknown Account";

            await onConfirm(selectedEmployeeIds, {
                payment_date: paymentDate,
                payment_mode: paymentMode,
                paid_through_id: paidThroughId,
                paid_through_name: accountName,
                reference_id: referenceId,
                notes: notes
            });
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setSubmitting(false);
        }
    };


    const formatCurrency = (amount: number) => {
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            title={<Title order={4}>Record Payment</Title>}
            size="xl"
            closeOnClickOutside={false}
        >
            <Stack spacing="md">
                {/* Summary Section */}
                <Paper p="md" className={classes.summaryCard} radius="md">
                    <Group position="apart">

                        <Text size="sm" weight={600}>Total Amount</Text>
                        <Text size="xl" weight={700}>{formatCurrency(calculateTotal())}</Text>

                    </Group>
                </Paper>

                <Group grow align="flex-start">
                    <DateInput
                        label="Payment Date"
                        placeholder="Select date"
                        value={paymentDate}
                        onChange={setPaymentDate}
                        icon={<IconCalendar size={16} />}
                        withAsterisk
                    />
                    <Select
                        label="Paid Through"
                        placeholder="Select Account"
                        data={paymentAccountList.map(account => ({
                            value: account.id || account.value,
                            label: account.accountName || account.label || account.nickname || "Unknown Account"
                        }))}
                        value={paidThroughId}
                        onChange={setPaidThroughId}
                        searchable
                        withAsterisk
                    />
                </Group>



                <Textarea
                    label="Notes"
                    placeholder="Any additional notes"
                    value={notes}
                    onChange={(e) => setNotes(e.currentTarget.value)}
                    minRows={2}
                />



                <Group position="right" mt="md">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        loading={submitting}
                        leftIcon={<IconCheck size={16} />}
                    >
                        Submit
                    </Button>
                </Group>
            </Stack>
        </Modal>
    );
}
